<template>
  <MDBCol :lg="props.lg" :md="props.md" class="mb-4">
    <a :href="resolvedUrl"
      :download="downloadName"
      @click.prevent="downloadFile"
      class="file btn btn-dark d-flex align-items-center justify-content-stretch position-relative w-100 p-4">
      <img v-if="props.file.cover || props.file.poster"
        :src="props.file.cover ? getImage(props.file.cover) : props.file.poster" class="cover" alt="cover" />

      <MDBIcon :icon="`file-${setFileType(props.file.type ?? props.file.extension)}`"
        class="icon-extension d-flex align-items-center justify-content-center" />
      <MDBRow class="align-items-center text-start w-100">
        <MDBCol col="3" class="text-center">
          <MDBIcon icon="download" class="icon d-inline-flex align-items-center justify-content-center" />
        </MDBCol>
        <MDBCol col="7">
          <h6 v-html="props.file.name ?? props.file.title" class="file-title text-white"></h6>
          <p v-if="props.file.description" v-html="props.file.description" class="fw-bold mb-0"></p>
          <p v-html="props.file.date" class="fw-bold mb-0"></p>
        </MDBCol>
      </MDBRow>
    </a>
  </MDBCol>
</template>

<script setup>
import { MDBRow, MDBCol, MDBIcon } from "mdb-vue-ui-kit";
import { computed, defineProps } from "vue";

const props = defineProps({
  file: Object,
  lg: {
    type: Number,
    default: 4
  },
  md: {
    type: Number,
    default: 6
  }
});

const getImage = (file) => require("../../assets/img/preislisten/" + file);

const setFileType = (type) => {
  if (type == "xlsx") {
    return "excel";
  } else {
    return type;
  }
};

const downloadUrl = computed(() =>
  props.file?.simpleLink
    ? `/files/preislist/${props.file.directory}${props.file.file}`
    : props.file?.file
);

const sanitizeTitle = (title) => {
  if (!title || typeof title !== "string") {
    return "";
  }

  return title.replace(/<[^>]*>/g, "").trim();
};

const extractExtensionFromPath = (path) => {
  if (!path || typeof path !== "string") {
    return "";
  }

  const segments = path.split(".");
  return segments.length > 1 ? segments.pop() : "";
};

const downloadName = computed(() => {
  const rawTitle = props.file?.name ?? props.file?.title ?? "";
  const title = sanitizeTitle(rawTitle) || "file";

  const extension =
    extractExtensionFromPath(props.file?.file) ||
    (props.file?.extension && props.file.extension.replace(/^\./, "")) ||
    (props.file?.type && props.file.type.replace(/^\./, ""))
    ;

  return extension ? `${title}.${extension}` : title;
});

const buildUrlWithContentDisposition = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url, window.location.href);
    // rscd is Azure Blob shorthand for response-content-disposition
    const encoded = encodeURIComponent(downloadName.value);
    const disposition = `attachment; filename="${downloadName.value}"; filename*=UTF-8''${encoded}`;
    u.searchParams.set("rscd", disposition);
    return u.toString();
  } catch (_) {
    return url;
  }
};

const resolvedUrl = computed(() => {
  const url = downloadUrl.value;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) {
      return buildUrlWithContentDisposition(url);
    }
  } catch (_) {
    return url;
  }
  return url;
});

const downloadFile = async () => {
  const url = resolvedUrl.value;

  // If cross-origin, let the browser handle it to avoid CORS errors.
  try {
    const parsedUrl = new URL(url, window.location.href);
    if (parsedUrl.origin !== window.location.origin) {
      window.open(url, "_blank");
      return;
    }
  } catch (error) {
    window.open(url, "_blank");
    return;
  }

  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadName.value;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    // Fallback to default behavior if fetch is blocked
    window.open(url, "_blank");
  }
};
</script>

<style scoped lang="scss">
@import "../../styles/_variables.scss";

.icon {
  font-size: 20px;
  background: white;
  color: $darkGray;
  border-radius: 100px;
  height: 40px;
  width: 40px;
  transform: scale(1);
  transition: 150ms ease-in-out;
}

.icon-extension {
  display: none;
  font-size: 20px;
  background: white;
  color: $darkGray;
  height: 40px;
  width: 80px;
  position: absolute;
  top: 0;
  right: 0;

  @media screen and (max-width: $xxxlBreakpoint) and (min-width: ($xlBreakpoint + 1)) {
    height: 30px;
    width: 60px;
    font-size: 17px;
  }

  @media screen and (max-width: $xlBreakpoint) and (min-width: $lgBreakpoint) {
    height: 20px;
    width: 40px;
    font-size: 12px;
  }
}

.file {
  min-height: 150px;
  height: 100%;
  background-color: $darkGray;
  z-index: 9;

  &:hover {
    background: darken($darkGray, 5%);

    .icon {
      transform: scale(1.1);
      color: $dangerRed;
    }
  }
}

.cover {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  z-index: 10;
}
</style>
